'use client'

import type { ReactNode } from 'react'
import { motion } from 'motion/react'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { MainNavItem, MainNavRenderLink } from './types'
import { defaultRenderLink } from './render-link'

export interface MainNavBarProps {
  /**
   * `top`: the laptop bar (leading on the left, the items and trailing on the right).
   * `top-compact`: the HP top bar (leading and trailing only; the items live in the bottom bar).
   * `bottom`: the HP bottom bar (the items, then trailing if given).
   */
  layout: 'top' | 'top-compact' | 'bottom'
  items: MainNavItem[]
  /** Logo and school picker. */
  leading?: ReactNode
  /** Usually the avatar menu. */
  trailing?: ReactNode
  renderLink?: MainNavRenderLink
  /** Called when any item is clicked (apps use it to reset the idle-hide timer). */
  onNavigate?: () => void
  className?: string
}

/**
 * The main menu bar, presentational only: no data fetching, no positioning. The app decides where it
 * sits (fixed, idle-hide) and which items each account type gets (PIB-35: Anak 4, Orang tua 5,
 * Guru 5, Pemilik/Admin 6).
 */
export function MainNavBar({
  layout,
  items,
  leading,
  trailing,
  renderLink = defaultRenderLink,
  onNavigate,
  className,
}: MainNavBarProps) {
  const navItems = items.map((item) => (
    <MainNavBarItem key={item.href} item={item} renderLink={renderLink} onNavigate={onNavigate} />
  ))

  if (layout === 'bottom') {
    return (
      <nav
        className={cn(
          'srd:bg-neutral-white srd:border-outline-variant/50 srd:flex srd:h-[90px] srd:w-full srd:items-center srd:justify-between srd:border-t srd:px-2 srd:pt-2 srd:pb-8',
          className,
        )}
      >
        {navItems}
        {trailing}
      </nav>
    )
  }

  if (layout === 'top-compact') {
    return (
      <div
        className={cn(
          'srd:bg-neutral-white srd:border-outline-variant/50 srd:flex srd:h-16 srd:w-full srd:items-center srd:justify-between srd:gap-x-4 srd:border-b srd:px-2',
          className,
        )}
      >
        <div className='srd:flex srd:min-w-0 srd:flex-1 srd:items-center srd:gap-2'>{leading}</div>
        {trailing}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'srd:bg-neutral-white srd:border-outline-variant/50 srd:grid srd:h-18 srd:w-full srd:grid-cols-[1fr_1.5fr] srd:items-center srd:gap-x-4 srd:border-b srd:px-4 srd:py-1.5 srd:shadow-inner',
        className,
      )}
    >
      <div className='srd:flex srd:w-full srd:min-w-0 srd:items-center srd:gap-2'>{leading}</div>
      <nav className='srd:flex srd:w-full srd:items-center srd:justify-end'>
        <div className='srd:flex srd:h-full srd:w-full srd:max-w-xl srd:items-center srd:justify-between srd:gap-x-4'>
          {navItems}
          {trailing}
        </div>
      </nav>
    </div>
  )
}

interface MainNavBarItemProps {
  item: MainNavItem
  renderLink: MainNavRenderLink
  onNavigate?: () => void
}

function MainNavBarItem({ item, renderLink, onNavigate }: MainNavBarItemProps) {
  const Icon = item.icon

  return renderLink({
    href: item.href,
    className: 'srd:h-fit srd:w-fit',
    onClick: onNavigate,
    children: (
      <motion.span
        aria-current={item.isActive ? 'page' : undefined}
        className='srd:text-neutral-black srd:flex srd:flex-col srd:items-center srd:gap-y-1'
        whileHover={{ scale: item.isActive ? 1 : 1.05 }}
        whileTap={{ scale: item.isActive ? 1 : 0.95 }}
      >
        <span className='srd:relative srd:flex srd:h-fit srd:w-fit srd:items-center srd:justify-center srd:rounded-2xl srd:p-2'>
          {item.isActive && (
            <motion.span
              layoutId='main-nav-active-bg'
              className='srd:absolute srd:inset-0 srd:w-14 srd:-translate-x-2 srd:rounded-2xl srd:bg-blue-100/20'
              transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
            />
          )}
          <Icon size={24} className='srd:text-neutral-black srd:relative srd:z-10' />
          {item.badge && (
            <span className='srd:absolute srd:top-0 srd:right-0 srd:z-20 srd:translate-x-1/2 srd:-translate-y-1'>
              {item.badge}
            </span>
          )}
        </span>
        <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:relative srd:z-10')}>{item.label}</span>
      </motion.span>
    ),
  })
}
